import { head, includes, split } from "lodash";
import { tint } from "@glossy";

export enum Topic {
  Business = "business",
  Ecology = "ecology",
  Entertainment = "entertainment",
  Sport = "sport",
  Lifestyle = "lifestyle",
  Medias = "medias",
  Politics = "politics",
  Society = "society",
  Technology = "technology",
}

const Topics = Object.keys(Topic)
  .map((k) => Topic[k as keyof typeof Topic])
  .map((v) => v as Topic);

export enum Category {
  // Business
  Luxury = "business.luxury",
  Market = "business.market",
  Transport = "business.transport",
  YourMoney = "business.your-money",
  // Ecology
  Biodiversity = "ecology.biodiversity",
  Climatic = "ecology.climatic",
  Energy = "ecology.energy",
  Farming = "ecology.farming",
  NaturalDisaster = "ecology.natural-disaster",
  PollutionRecycling = "ecology.pollution-recycling",
  // Entertainment
  Arts = "entertainment.arts",
  Books = "entertainment.books",
  Comics = "entertainment.comics",
  History = "entertainment.history",
  Movies = "entertainment.movies",
  Music = "entertainment.music",
  TheaterDance = "entertainment.theater-dance",
  TvRadio = "entertainment.tv-radio",
  VideoGames = "entertainment.video-games",
  // Lifestyle
  AutoMoto = "lifestyle.auto-moto",
  Beauty = "lifestyle.beauty",
  Clock = "lifestyle.clock",
  Family = "lifestyle.family",
  Fashion = "lifestyle.fashion",
  Food = "lifestyle.food",
  HomeGarden = "lifestyle.home-garden",
  People = "lifestyle.people",
  ProfessionalLife = "lifestyle.professional-life",
  Seduction = "lifestyle.seduction",
  Travel = "lifestyle.travel",
  Wedding = "lifestyle.wedding",
  Wellness = "lifestyle.wellness",
  // Medias
  Buzz = "medias.buzz",
  Communication = "medias.communication",
  Medias = "medias.medias",
  // Politics
  PoliticsAfrica = "politics.africa",
  PoliticsAmerica = "politics.americas",
  PoliticsAsiaPacific = "politics.asia-pacific",
  PoliticsEurope = "politics.europe",
  PoliticsFrance = "politics.france",
  PoliticsMiddleEast = "politics.middle-east",
  PoliticsUsa = "politics.usa",
  PoliticsGermany = "politics.germany",
  PoliticsChina = "politics.china",
  // Society
  Education = "society.education",
  Employment = "society.employment",
  Health = "society.health",
  Justice = "society.justice",
  Security = "society.security",
  Social = "society.social",
  // Sport
  AmericanFootball = "sport.american-football",
  Archery = "sport.archery",
  Athletics = "sport.athletics",
  Badminton = "sport.badminton",
  Baseball = "sport.baseball",
  Basketball = "sport.basketball",
  Biathlon = "sport.biathlon",
  Bobsleigh = "sport.bobsleigh",
  Bodyboard = "sport.bodyboard",
  Boxing = "sport.boxing",
  Canoeing = "sport.canoeing",
  cricket = "sport.cricket",
  CrosscountrySkating = "sport.crosscountry-skating",
  Curling = "sport.curling",
  Cycling = "sport.cycling",
  DisabilitySport = "sport.disability-sport",
  Diving = "sport.diving",
  Equestrian = "sport.equestrian",
  Fencing = "sport.fencing",
  FigureSkating = "sport.figure-skating",
  Football = "sport.football",
  Formula1 = "sport.formula1",
  GaelicGames = "sport.gaelic-games",
  Golf = "sport.golf",
  Gymnastics = "sport.gymnastics",
  Handball = "sport.handball",
  Hockey = "sport.hockey",
  HorseRacing = "sport.horse-racing",
  IceHockey = "sport.ice-hockey",
  Judo = "sport.judo",
  Kitesurf = "sport.kitesurf",
  Motorsport = "sport.motorsport",
  Olympics = "sport.olympics",
  Rowing = "sport.rowing",
  Rugby = "sport.rugby",
  Sailing = "sport.sailing",
  Skateboard = "sport.skateboard",
  SkiJumping = "sport.ski-jumping",
  Snooker = "sport.snooker",
  Snowboard = "sport.snowboard",
  Squash = "sport.squash",
  Surf = "sport.surf",
  Swimming = "sport.swimming",
  PingPong = "sport.ping-pong",
  Taekwondo = "sport.taekwondo",
  Tennis = "sport.tennis",
  Volleyball = "sport.volleyball",
  Windsurf = "sport.windsurf",
  WinterSport = "sport.winter-sport",
  // Technology
  Computer = "technology.computer",
  MobileDevice = "technology.mobile-device",
  Science = "technology.science",
  Startup = "technology.startup-digital",
}

const Tree: Record<Topic, Category[]> = {
  [Topic.Business]: [
    Category.Luxury,
    Category.Market,
    Category.Transport,
    Category.YourMoney,
  ],
  [Topic.Ecology]: [
    Category.Biodiversity,
    Category.Climatic,
    Category.Energy,
    Category.Farming,
    Category.NaturalDisaster,
    Category.PollutionRecycling,
  ],
  [Topic.Entertainment]: [
    Category.Arts,
    Category.Books,
    Category.Comics,
    Category.History,
    Category.Movies,
    Category.Music,
    Category.TheaterDance,
    Category.TvRadio,
    Category.VideoGames,
  ],
  [Topic.Lifestyle]: [
    Category.AutoMoto,
    Category.Beauty,
    Category.Clock,
    Category.Family,
    Category.Fashion,
    Category.Food,
    Category.HomeGarden,
    Category.People,
    Category.ProfessionalLife,
    Category.Seduction,
    Category.Travel,
    Category.Wedding,
    Category.Wellness,
  ],
  [Topic.Medias]: [Category.Buzz, Category.Communication, Category.Medias],
  [Topic.Politics]: [
    Category.PoliticsAfrica,
    Category.PoliticsAmerica,
    Category.PoliticsAsiaPacific,
    Category.PoliticsEurope,
    Category.PoliticsFrance,
    Category.PoliticsMiddleEast,
    Category.PoliticsUsa,
    Category.PoliticsGermany,
    Category.PoliticsChina,
  ],
  [Topic.Society]: [
    Category.Education,
    Category.Employment,
    Category.Health,
    Category.Justice,
    Category.Security,
    Category.Social,
  ],
  [Topic.Sport]: [
    Category.AmericanFootball,
    Category.Archery,
    Category.Athletics,
    Category.Badminton,
    Category.Baseball,
    Category.Basketball,
    Category.Biathlon,
    Category.Bobsleigh,
    Category.Bodyboard,
    Category.Boxing,
    Category.Canoeing,
    Category.cricket,
    Category.CrosscountrySkating,
    Category.Curling,
    Category.Cycling,
    Category.DisabilitySport,
    Category.Diving,
    Category.Equestrian,
    Category.Fencing,
    Category.FigureSkating,
    Category.Football,
    Category.Formula1,
    Category.GaelicGames,
    Category.Golf,
    Category.Gymnastics,
    Category.Handball,
    Category.Hockey,
    Category.HorseRacing,
    Category.IceHockey,
    Category.Judo,
    Category.Kitesurf,
    Category.Motorsport,
    Category.Olympics,
    Category.Rowing,
    Category.Rugby,
    Category.Sailing,
    Category.Skateboard,
    Category.SkiJumping,
    Category.Snooker,
    Category.Snowboard,
    Category.Squash,
    Category.Surf,
    Category.Swimming,
    Category.PingPong,
    Category.Taekwondo,
    Category.Tennis,
    Category.Volleyball,
    Category.Windsurf,
    Category.WinterSport,
  ],
  [Topic.Technology]: [
    Category.Computer,
    Category.MobileDevice,
    Category.Science,
    Category.Startup,
  ],
};

const Colors: Record<Topic, string> = {
  [Topic.Business]: "#8742C6",
  [Topic.Ecology]: "#739927",
  [Topic.Entertainment]: "#924908",
  [Topic.Lifestyle]: "#8E2436",
  [Topic.Medias]: "#C09900",
  [Topic.Politics]: "#950077",
  [Topic.Society]: "#008542",
  [Topic.Sport]: "#113F5F",
  [Topic.Technology]: "#00D2A3",
};

type TopicOrCategory = Topic | Category;

export function getCategories(topic: Topic): Category[] {
  return Tree[topic];
}

export function getTopicFromCategory(topic: Category): Topic {
  const root = head(split(topic, "."));
  return root as Topic;
}

export function getTopicCategoryColor(topic: TopicOrCategory) {
  if (isTopic(topic)) {
    return Colors[topic];
  } else {
    const category = topic as Category;
    const root = getTopicFromCategory(category);
    const categories = getCategories(root);
    const index = categories.indexOf(category);
    const step = 50 / categories.length;
    return tint((index * step) / 100, Colors[root]);
  }
}

function isTopic(topic: TopicOrCategory): topic is Topic {
  return includes(Topics, topic);
}

export function getAllTopicCategories() {
  return Object.keys(Category)
    .map((k) => Category[k as keyof typeof Category])
    .map((v) => v as Category);
}
